import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, ChartLine, Users, Terminal, Zap, X, Loader2, Plus } from "lucide-react";

/** Wrapper button that gates on Pro. If not pro, links to /pro. */
export function ProGatedButton({ label, icon, onClick, testId, className = "" }) {
  const { user } = useAuth();
  if (!user?.pro_active) {
    return (
      <Link to="/pro" data-testid={`${testId}-pro-locked`}
        className={`btn-outline flex items-center gap-2 hover:!border-[var(--accent)] hover:!text-[var(--accent)] ${className}`}>
        {icon}
        <span>{label}</span>
        <Zap size={12} className="text-[var(--accent)]"/>
      </Link>
    );
  }
  return (
    <button data-testid={testId} onClick={onClick}
      className={`btn-outline flex items-center gap-2 hover:!border-[var(--accent)] hover:!text-[var(--accent)] ${className}`}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

/** Compile check result panel */
export function CompileCheckPanel({ result, onClose }) {
  if (!result) return null;
  const icon = result.ok
    ? <CheckCircle2 className="text-[var(--profit)]" size={20}/>
    : <XCircle className="text-[var(--loss)]" size={20}/>;
  const isRealMT = result.source === "metaeditor_worker";
  const isFallback = result.source === "regex_fallback";
  return (
    <div className="card-sharp p-5" data-testid="compile-check-result">
      <div className="flex items-center gap-3 mb-3">
        {icon}
        <div className="flex-1">
          <div className="font-display text-lg text-white flex items-center gap-2">
            {result.ok ? "Compile check passed" : "Compile check failed"}
            {isRealMT && <span className="chip !border-[var(--profit)]/40 !text-[var(--profit)]" data-testid="cc-source-mt">◆ MetaEditor</span>}
            {isFallback && <span className="chip !border-[var(--warning)]/40 !text-[var(--warning)]" data-testid="cc-source-fallback">Regex fallback</span>}
          </div>
          <div className="text-xs font-mono text-[var(--text-secondary)]">
            {result.error_count} errors · {result.warning_count} warnings
            {isFallback && result.fallback_reason && <span className="text-[var(--warning)] block mt-0.5">{result.fallback_reason}</span>}
          </div>
        </div>
        <button data-testid="compile-check-close" onClick={onClose}
          className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
      </div>
      {result.findings?.length > 0 && (
        <div className="space-y-1.5 max-h-[240px] overflow-auto">
          {result.findings.map((f, i) => (
            <div key={`${f.severity}-${f.line || 0}-${i}`} className="flex gap-2 text-xs">
              {f.severity === "error"
                ? <XCircle size={13} className="text-[var(--loss)] mt-0.5 shrink-0"/>
                : <AlertTriangle size={13} className="text-[var(--warning)] mt-0.5 shrink-0"/>}
              <div className="font-mono">
                {f.line && <span className="text-[var(--text-muted)]">L{f.line}: </span>}
                <span className="text-[var(--text-secondary)]">{f.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {result.findings?.length === 0 && (
        <p className="text-xs text-[var(--text-secondary)] font-mono">No issues found. Ship it.</p>
      )}
    </div>
  );
}

/** Backtest panel with mini equity curve */
export function BacktestPanel({ result, onClose }) {
  if (!result) return null;
  const pnlPositive = result.net_usd >= 0;
  const curve = result.equity_curve || [];
  const minE = Math.min(...curve.map(p => p.equity), result.starting_equity);
  const maxE = Math.max(...curve.map(p => p.equity), result.starting_equity);
  const range = Math.max(maxE - minE, 1);
  const path = curve.map((p, i) => {
    const x = (i / Math.max(curve.length - 1, 1)) * 100;
    const y = 100 - ((p.equity - minE) / range) * 100;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
  return (
    <div className="card-sharp p-5" data-testid="backtest-result">
      <div className="flex items-center gap-3 mb-4">
        <ChartLine className="text-[var(--accent)]" size={20}/>
        <div className="flex-1">
          <div className="font-display text-lg text-white">Backtest results</div>
          <div className="text-xs font-mono text-[var(--text-muted)]">{result.candles} synthetic candles · seed {result.seed}</div>
        </div>
        <button data-testid="backtest-close" onClick={onClose}
          className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Trades" value={result.trades} testId="bt-stat-trades"/>
        <Stat label="Win rate" value={`${result.win_rate}%`} testId="bt-stat-winrate"/>
        <Stat label="Net P&L" value={`${pnlPositive ? "+" : ""}$${result.net_usd.toFixed(2)}`}
          highlight={pnlPositive ? "profit" : "loss"} testId="bt-stat-pnl"/>
        <Stat label="Max DD" value={`-$${result.max_drawdown_usd.toFixed(2)}`} testId="bt-stat-dd"/>
      </div>

      <div className="h-32 bg-black/40 rounded border border-white/5 relative overflow-hidden" data-testid="bt-equity-chart">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          <path d={path} fill="none" stroke={pnlPositive ? "#00FF66" : "#FF3366"} strokeWidth="0.6" vectorEffect="non-scaling-stroke"/>
        </svg>
      </div>

      {result.trade_log?.length > 0 && (
        <div className="mt-4 border-t border-white/5 pt-4" data-testid="bt-trade-log">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-secondary)]">Trade log</span>
            <span className="chip">{result.trade_log.length} entries</span>
          </div>
          <div className="max-h-56 overflow-auto rounded border border-white/5">
            <table className="w-full text-[11px] font-mono">
              <thead className="text-[var(--text-muted)] uppercase tracking-widest sticky top-0 bg-[#0B0D12]">
                <tr>
                  <th className="text-left px-2 py-1.5">#</th>
                  <th className="text-left">Side</th>
                  <th className="text-right">Entry</th>
                  <th className="text-right">Exit</th>
                  <th className="text-right">Bars</th>
                  <th className="text-right">Reason</th>
                  <th className="text-right">Pips</th>
                  <th className="text-right px-2">P&L</th>
                </tr>
              </thead>
              <tbody>
                {result.trade_log.map((t, i) => (
                  <tr key={`${t.entry_i}-${t.exit_i}-${i}`} className={`border-t border-white/5 ${i % 2 === 0 ? "bg-black/20" : ""}`} data-testid={`bt-trade-${i}`}>
                    <td className="px-2 py-1.5 text-[var(--text-muted)]">{i + 1}</td>
                    <td className={t.side === "buy" ? "text-[var(--profit)]" : "text-[var(--loss)]"}>{t.side.toUpperCase()}</td>
                    <td className="text-right text-white">{t.entry_price}</td>
                    <td className="text-right text-white">{t.exit_price}</td>
                    <td className="text-right text-[var(--text-secondary)]">{t.bars_held}</td>
                    <td className={`text-right uppercase ${t.exit_reason === "tp" ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>{t.exit_reason}</td>
                    <td className={`text-right ${t.pnl_pips > 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                      {t.pnl_pips > 0 ? "+" : ""}{t.pnl_pips}
                    </td>
                    <td className={`text-right px-2 ${t.pnl_usd > 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                      {t.pnl_usd > 0 ? "+" : ""}${t.pnl_usd}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="mt-3 text-[11px] font-mono text-[var(--text-muted)] italic">
        {result.notice}
      </p>
    </div>
  );
}

function Stat({ label, value, highlight, testId }) {
  const color = highlight === "profit" ? "text-[var(--profit)]" : highlight === "loss" ? "text-[var(--loss)]" : "text-white";
  return (
    <div className="border border-white/10 rounded p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

/** Team-share modal (private email + public gallery) */
export function ShareModal({ strategyId, strategyName, open, onClose }) {
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState("");
  const [perm, setPerm] = useState("view");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [publicInfo, setPublicInfo] = useState({ is_public: false, public_slug: null });
  const [publishBusy, setPublishBusy] = useState(false);

  React.useEffect(() => {
    if (!open || !strategyId) return;
    (async () => {
      try {
        const [shr, strat] = await Promise.all([
          api.get(`/strategies/${strategyId}/shares`),
          api.get(`/strategies/${strategyId}`),
        ]);
        setShares(shr.data); setLoaded(true);
        setPublicInfo({ is_public: !!strat.data.is_public, public_slug: strat.data.public_slug || null });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load shares");
      }
    })();
  }, [open, strategyId]);

  const togglePublish = async () => {
    setPublishBusy(true);
    try {
      if (publicInfo.is_public) {
        await api.post(`/strategies/${strategyId}/unpublish`);
        setPublicInfo({ is_public: false, public_slug: publicInfo.public_slug });
        toast.success("Strategy is now private");
      } else {
        const { data } = await api.post(`/strategies/${strategyId}/publish`);
        setPublicInfo({ is_public: true, public_slug: data.public_slug });
        toast.success("Published to gallery");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setPublishBusy(false); }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}/s/${publicInfo.public_slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch { toast.info(url); }
  };

  if (!open) return null;

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post(`/strategies/${strategyId}/share`, { email: email.trim(), permission: perm });
      const { data } = await api.get(`/strategies/${strategyId}/shares`);
      setShares(data); setEmail("");
      toast.success(`Shared with ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setBusy(false); }
  };

  const revoke = async (shareId) => {
    try {
      await api.delete(`/shares/${shareId}`);
      setShares(s => s.filter(x => x.share_id !== shareId));
      toast.success("Access revoked");
    } catch { toast.error("Revoke failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="share-modal" onClick={onClose}>
      <div className="card-sharp p-6 max-w-lg w-full" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[var(--accent)]"/>
            <h3 className="font-display text-xl text-white">Share <span className="text-[var(--accent)]">{strategyName}</span></h3>
          </div>
          <button data-testid="share-close" onClick={onClose}
            className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
        </div>

        <div className="space-y-3">
          <div className="border border-white/10 rounded p-4 bg-black/30" data-testid="publish-section">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h4 className="font-display text-white text-base flex items-center gap-2">
                  Public gallery
                  <span className={`chip ${publicInfo.is_public ? "!border-[var(--profit)]/40 !text-[var(--profit)]" : ""}`}>
                    {publicInfo.is_public ? "PUBLIC" : "PRIVATE"}
                  </span>
                </h4>
                <p className="text-xs text-[var(--text-muted)] font-mono mt-1 leading-relaxed">
                  {publicInfo.is_public
                    ? "Anyone can view & fork this strategy from the gallery."
                    : "Publish to let the community discover and fork your strategy."}
                </p>
                {publicInfo.is_public && publicInfo.public_slug && (
                  <button data-testid="copy-public-link" onClick={copyPublicLink}
                    className="mt-2 text-[11px] font-mono text-[var(--accent)] hover:underline">
                    /s/{publicInfo.public_slug} · click to copy
                  </button>
                )}
              </div>
              <button data-testid="toggle-publish-btn" onClick={togglePublish} disabled={publishBusy}
                className={publicInfo.is_public ? "btn-outline text-xs !border-[var(--loss)]/40 hover:!text-[var(--loss)]" : "btn-primary text-xs"}>
                {publishBusy ? "…" : (publicInfo.is_public ? "Unpublish" : "Publish")}
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-white/5">
            <input data-testid="share-email-input" type="email" placeholder="teammate@example.com"
              value={email} onChange={(e)=>setEmail(e.target.value)}
              className="input-dark flex-1"/>
            <select data-testid="share-perm-select" value={perm} onChange={(e)=>setPerm(e.target.value)} className="input-dark !w-28">
              <option value="view">View</option>
              <option value="edit">Edit</option>
            </select>
            <button data-testid="share-add-btn" onClick={add} disabled={busy}
              className="btn-primary flex items-center gap-2"><Plus size={14}/> Share</button>
          </div>

          {loaded && shares.length === 0 && (
            <p className="text-xs font-mono text-[var(--text-muted)] italic mt-4" data-testid="share-empty">
              Not shared with anyone yet.
            </p>
          )}
          <div className="space-y-2 mt-3">
            {shares.map((s) => (
              <div key={s.share_id} className="border border-white/10 rounded p-3 flex items-center gap-3"
                data-testid={`share-row-${s.share_id}`}>
                <div className="flex-1">
                  <div className="text-sm text-white">{s.shared_email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{s.permission}</div>
                </div>
                <button data-testid={`share-revoke-${s.share_id}`} onClick={()=>revoke(s.share_id)}
                  className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]">
                  <X size={13}/>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
